import { DateTime } from 'luxon';
import { fromISO } from 'utils/luxon-helpers';

export const dayTools = {
  unix: (): number => Math.round(+new Date() / 1000),
  today: (): string => {
    return new Date().toISOString().slice(0, 10);
  },
  isValidDay: (day: string): boolean => {
    return DateTime.fromISO(day).isValid;
  },

  /**
   * Compares two 'day' strings shouldBeBefore to shouldBeAfter
   * Days are 'YYYY-MM-DD'
   *
   * @param {string} shouldBeBefore - YYYY-MM-DD
   * @param {string} shouldBeAfter  - YYYY-MM-DD
   *
   * @returns boolean
   *
   * @example
   * isBefore('2010-01-03', '2011-02-02') #=> True
   * isBefore('2013-03-03', '2011-02-02') #=> False
   */
  isBefore: (shouldBeBefore: string, shouldBeAfter: string) => {
    return new Date(shouldBeBefore).toISOString() < new Date(shouldBeAfter).toISOString();
  },
  isAfter: (shouldBeAfter: string, shouldBeBefore: string) => {
    return new Date(shouldBeAfter).toISOString() > new Date(shouldBeBefore).toISOString();
  },
  validateDay: (day: string) => {
    if (!dayTools.isValidDay(day)) {
      throw new Error(`${day} is not a valid day`);
    }
  },

  fromDay: (day: string): DateTime<true> => {
    return fromISO(day);
  },

  addWeek: (day: string): string => {
    return fromISO(day).plus({ week: 1 }).toISODate();
  },

  subtractWeek: (day: string): string => {
    return fromISO(day).minus({ week: 1 }).toISODate();
  },

  addDay: (day: string): string => {
    return fromISO(day).plus({ day: 1 }).toISODate();
  },

  subtractDay: (day: string): string => {
    return fromISO(day).minus({ day: 1 }).toISODate();
  },

  addMonth: (day: string): string => {
    return fromISO(day).plus({ month: 1 }).toISODate();
  },

  subtractMonth: (day: string): string => {
    return fromISO(day).minus({ month: 1 }).toISODate();
  },

  format: (day: string | Date): string => {
    return new Date(day).toISOString().slice(0, 10);
  },
};

export const datetimeTools = {
  isValidDateTime: (datetime: string): boolean => {
    return DateTime.fromISO(datetime).isValid;
  },

  fromDatetime: (datetime: string): DateTime<true> => {
    return fromISO(datetime);
  },

  setDay: (datetime: string, day: string): string => {
    const toDay = dayTools.fromDay(day);
    const newDate = datetimeTools.fromDatetime(datetime).set({
      year: toDay.year,
      month: toDay.month,
      day: toDay.day,
    });

    return datetimeTools.asString(newDate);
  },

  secondsBetween: (from: string, to: string): number => {
    const fromDatetime = datetimeTools.fromDatetime(from);
    const toDatetime = datetimeTools.fromDatetime(to);
    return toDatetime.diff(fromDatetime, 'seconds').seconds;
  },

  addSeconds: (datetime: string, seconds: number): string => {
    const output = datetimeTools.fromDatetime(datetime).plus({ seconds });
    return datetimeTools.asString(output);
  },

  asString: (m: DateTime<true>): string => {
    return m.toISO();
  },

  isBetween: (shouldBeBetween: string, start: string, end: string) => {
    const between = new Date(shouldBeBetween);
    const startDate = new Date(start);
    const endDate = new Date(end);
    return between >= startDate && between <= endDate;
  },
};
